import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { SectionCard } from '../components/sectionCard/SectionCard';

import * as Styled from './WorkerTab.styles';
import {
  UNKNOWN_VALUE,
  WorkerProcessStatus,
  ageMsSince,
  bullQueueList,
  fillRatio,
  formatBytes,
  formatDurationMs,
  formatLimiter,
  formatSweepInterval,
} from './worker.helpers';

export type WorkerProcessCardProps = {
  status: WorkerProcessStatus;
  ageMs: number | null;
  now: number;
};

/** Process half of the RCP worker tab: pod identity, uptime and the four runtime gauges. */
export const WorkerProcessCard = ({ status, ageMs, now }: WorkerProcessCardProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const queues = bullQueueList(status.bull);
  const uptimeMs = ageMsSince(status.startedAt, now);

  /**
   * The tuning line under a Bull queue, e.g. "limiter 20/s" or "sweep 120 s".
   *
   * `limiter` and `sweep` are the worker's own vocabulary, the same words the protocol and the
   * chart use, so they are not translated -- an operator comparing this screen with a values file
   * or a log line reads exactly the same token. Nothing renders when the worker published no
   * config, which is how an older heartbeat stays honest instead of claiming a default.
   */
  const tuningFor = (queue: string): string => {
    if (!status.config) {
      return '';
    }

    if (queue === 'rcp-server-order') {
      const limiter = formatLimiter(status.config.limiterMax, status.config.limiterDurationMs);

      return limiter === UNKNOWN_VALUE ? '' : `limiter ${limiter}`;
    }

    if (queue === 'dod-drain') {
      const sweep = formatSweepInterval(status.config, 'off');

      return sweep === UNKNOWN_VALUE ? '' : `sweep ${sweep}`;
    }

    return '';
  };

  return (
    <SectionCard>
      <Styled.ProcessGrid data-testid="dod-worker-process-card">
        <Styled.ProcessIdentity>
          <Styled.MetricLabel>{formatMessage({ id: AppMessages['dod.worker.process'] })}</Styled.MetricLabel>
          <Styled.PodName>{status.podName}</Styled.PodName>
          <Styled.MutedMono>{status.version}</Styled.MutedMono>
          <Styled.MetricDetail>
            {formatMessage(
              { id: AppMessages['dod.worker.uptimeLine'] },
              { uptime: formatDurationMs(uptimeMs), age: formatDurationMs(ageMs) },
            )}
          </Styled.MetricDetail>
        </Styled.ProcessIdentity>

        <div>
          <Styled.MetricLabel>{formatMessage({ id: AppMessages['dod.worker.eventLoopLag'] })}</Styled.MetricLabel>
          <Styled.MetricValue>{Math.round(status.eventLoopLagMs)} ms</Styled.MetricValue>
        </div>

        <div>
          <Styled.MetricLabel>{formatMessage({ id: AppMessages['dod.worker.dbPool'] })}</Styled.MetricLabel>
          <Styled.MetricValue>
            {status.dbPool.used ?? '—'} <small>/ {status.dbPool.max ?? '—'}</small>
          </Styled.MetricValue>
          <Styled.MetricDetail>{formatMessage({ id: AppMessages['dod.worker.dbPool.caption'] })}</Styled.MetricDetail>
          <Styled.Track>
            <Styled.TrackFill $ratio={fillRatio(status.dbPool.used, status.dbPool.max)} $tone="primary" />
          </Styled.Track>
        </div>

        {queues.slice(0, 2).map((queue) => (
          <div key={queue.queue}>
            <Styled.MetricLabel>
              {formatMessage({ id: AppMessages['dod.worker.bullQueue'] }, { queue: queue.queue })}
            </Styled.MetricLabel>
            <Styled.MetricValue>
              {queue.waiting} <small>{formatMessage({ id: AppMessages['dod.worker.waiting'] })}</small>
            </Styled.MetricValue>
            <Styled.MetricDetail>
              {formatMessage(
                { id: AppMessages['dod.worker.bullDetail'] },
                { active: queue.active, delayed: queue.delayed, failed: queue.failed },
              )}
            </Styled.MetricDetail>
            {tuningFor(queue.queue) && <Styled.MetricDetail>{tuningFor(queue.queue)}</Styled.MetricDetail>}
          </div>
        ))}

        <div>
          <Styled.MetricLabel>{formatMessage({ id: AppMessages['dod.worker.rss'] })}</Styled.MetricLabel>
          <Styled.MetricValue>{formatBytes(status.memoryRssBytes)}</Styled.MetricValue>
        </div>
      </Styled.ProcessGrid>
    </SectionCard>
  );
};
